/**
 * StandFull v2.0 - Controlled Logger
 * 
 * Logs apenas em modo DEV. Em produção, apenas erros são exibidos.
 * Substitui console.log espalhados pelo código.
 */

const isDev = import.meta.env.DEV;

type LogLevel = 'log' | 'warn' | 'error' | 'info';

interface LogOptions {
  /** Força log mesmo em produção (usar com cuidado) */
  force?: boolean;
}

/**
 * Log de desenvolvimento - só aparece em DEV
 * @example devLog('[usePedidos]', 'Query retornou', data.length, 'pedidos');
 */
export const devLog = (context: string, ...args: unknown[]): void => {
  if (!isDev) return;
  console.log(`%c${context}`, 'color: #6366f1; font-weight: bold;', ...args);
};

/**
 * Warning de desenvolvimento - só aparece em DEV
 * @example devWarn('[Auth]', 'Sessão expirada');
 */
export const devWarn = (context: string, ...args: unknown[]): void => {
  if (!isDev) return;
  console.warn(`%c${context}`, 'color: #f59e0b; font-weight: bold;', ...args);
};

/**
 * Erro - SEMPRE aparece (incluindo produção)
 * Use para erros críticos que precisam ser rastreados
 * @example devError('[usePedidos]', 'Falha ao salvar pedido', error);
 */
export const devError = (context: string, ...args: unknown[]): void => {
  console.error(`%c${context}`, 'color: #ef4444; font-weight: bold;', ...args);
};

/**
 * Info de desenvolvimento - só aparece em DEV
 * @example devInfo('[Realtime]', 'Conectado ao canal');
 */
export const devInfo = (context: string, ...args: unknown[]): void => {
  if (!isDev) return;
  console.info(`%c${context}`, 'color: #22c55e; font-weight: bold;', ...args);
};

/**
 * Log condicional com opções
 */
export const devLogIf = (
  condition: boolean,
  context: string,
  ...args: unknown[]
): void => {
  if (!condition) return;
  devLog(context, ...args);
};

/**
 * Agrupa logs relacionados (DEV only)
 */
export const devGroup = (label: string, fn: () => void): void => {
  if (!isDev) return;
  console.group(`%c${label}`, 'color: #8b5cf6; font-weight: bold;');
  fn();
  console.groupEnd();
};

/**
 * Timer para medir performance (DEV only)
 */
export const devTimer = (label: string): (() => void) => {
  if (!isDev) return () => {};
  const start = performance.now();
  return () => {
    const end = performance.now();
    devLog(`[Timer] ${label}`, `${(end - start).toFixed(2)}ms`);
  };
};

// Alias para compatibilidade
export const logger = {
  log: devLog,
  warn: devWarn,
  error: devError,
  info: devInfo,
  group: devGroup,
  timer: devTimer,
};

export default logger;
